import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import { Folder, Users, Lock, X, Loader2, Mail } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const FOLDER_COLORS = [
  '#6B7280', '#EF4444', '#F59E0B', '#10B981', '#3B82F6', 
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
];

// Create Folder Dialog
export const CreateLaunchpadFolderDialog = ({
  open,
  onOpenChange,
  onSubmit,
  loading
}) => {
  const [name, setName] = useState('');
  const [folderType, setFolderType] = useState('personal');
  const [color, setColor] = useState('#6B7280');

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), folder_type: folderType, color });
    setName('');
    setFolderType('personal');
    setColor('#6B7280');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-launchpad-folder-dialog">
        <DialogHeader>
          <DialogTitle>Create Launchpad Folder</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder Name</Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Links"
              data-testid="launchpad-folder-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Folder Type</Label>
            <Select value={folderType} onValueChange={setFolderType}>
              <SelectTrigger data-testid="launchpad-folder-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="personal">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    <span>Personal (Only you can see)</span>
                  </div>
                </SelectItem>
                <SelectItem value="shared">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>Shared (Share with others)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2 flex-wrap">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`w-8 h-8 rounded-full transition-all ${
                    color === c ? 'ring-2 ring-offset-2 ring-primary' : ''
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!name.trim() || loading}
            data-testid="create-launchpad-folder-submit"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Edit Folder Dialog
export const EditLaunchpadFolderDialog = ({
  open,
  onOpenChange,
  folder,
  onSave,
  onDelete,
  loading
}) => {
  const [name, setName] = useState('');
  const [color, setColor] = useState('#6B7280');

  useEffect(() => {
    if (folder) {
      setName(folder.name || '');
      setColor(folder.color || '#6B7280');
    }
  }, [folder]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-launchpad-folder-dialog">
        <DialogHeader>
          <DialogTitle>Edit Folder</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-folder-name">Folder Name</Label>
            <Input
              id="edit-folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-launchpad-folder-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2 flex-wrap">
              {FOLDER_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`w-8 h-8 rounded-full transition-all ${
                    color === c ? 'ring-2 ring-offset-2 ring-primary' : ''
                  }`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between">
          <Button 
            variant="destructive" 
            onClick={onDelete}
            disabled={loading}
          >
            Delete
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => onSave({ name: name.trim(), color })} 
              disabled={!name.trim() || loading}
              data-testid="save-launchpad-folder-btn"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Share Folder Dialog
export const ShareLaunchpadFolderDialog = ({
  open,
  onOpenChange,
  folder,
  onShare,
  onRemoveShare,
  loading
}) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [shares, setShares] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [permission, setPermission] = useState('read');
  const [loadingData, setLoadingData] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingByEmail, setInvitingByEmail] = useState(false);

  useEffect(() => {
    if (open && folder) {
      fetchData();
    }
  }, [open, folder]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      // Fetch shares
      const sharesRes = await api.get(`/launchpad/folders/${folder.launchpad_folder_id}/shares`);
      setShares(sharesRes.data.shares || []);
      
      // Only fetch users and groups for admins
      if (isAdmin) {
        const [usersRes, groupsRes] = await Promise.all([
          api.get('/users/all'),
          api.get('/admin/groups')
        ]);
        setUsers(usersRes.data.users || []);
        setGroups(groupsRes.data.groups || []);
      }
    } catch (error) {
      console.error('Failed to fetch share data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleShareByEmail = async () => {
    if (!inviteEmail.trim()) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setInvitingByEmail(true);
    try {
      await api.post(`/launchpad/folders/${folder.launchpad_folder_id}/share-by-email`, {
        email: inviteEmail.trim(),
        permission
      });
      toast.success('Folder shared with ' + inviteEmail);
      setInviteEmail('');
      fetchData(); // Refresh shares list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share folder');
    } finally {
      setInvitingByEmail(false);
    }
  };

  const handleShare = () => {
    onShare({
      user_ids: selectedUsers,
      group_ids: selectedGroups,
      permission
    });
    setSelectedUsers([]);
    setSelectedGroups([]);
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleGroup = (groupId) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  // Filter out already shared users/groups
  const availableUsers = users.filter(
    u => u.user_id !== folder?.owner_id && 
    !shares.some(s => s.entity_type === 'user' && s.entity_id === u.user_id)
  );
  const availableGroups = groups.filter(
    g => !shares.some(s => s.entity_type === 'group' && s.entity_id === g.group_id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="share-launchpad-folder-dialog">
        <DialogHeader>
          <DialogTitle>Share "{folder?.name}"</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Current Shares */}
          {shares.length > 0 && (
            <div className="space-y-2">
              <Label>Currently Shared With</Label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {shares.map((share, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-2 bg-muted rounded-md"
                  >
                    <div className="flex items-center gap-2">
                      {share.entity_type === 'user' ? (
                        <span className="text-sm">{share.name} ({share.email})</span>
                      ) : (
                        <span className="text-sm">{share.name} ({share.member_count} members)</span>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {share.permission}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => onRemoveShare(share)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email invite - available to all users */}
          <div className="space-y-2">
            <Label>Share by Email</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleShareByEmail()}
              />
              <Button 
                onClick={handleShareByEmail} 
                disabled={invitingByEmail || !inviteEmail.trim()}
                size="icon"
              >
                {invitingByEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Permission selector for email invite */}
          <div className="space-y-2">
            <Label>Permission Level</Label>
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="read">
                  Read Only - Can view and expand folder, see links
                </SelectItem>
                <SelectItem value="edit">
                  Edit Access - Can add, edit, and delete links
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Admin-only options: Multi-select users and groups */}
          {isAdmin && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Admin Options</span>
                </div>
              </div>

              {/* Add Users */}
              {availableUsers.length > 0 && (
                <div className="space-y-2">
                  <Label>Select Multiple Users</Label>
                  <ScrollArea className="h-32 border rounded-md p-2">
                    {availableUsers.map(user => (
                      <div 
                        key={user.user_id}
                        className="flex items-center gap-2 py-1"
                      >
                        <Checkbox
                          checked={selectedUsers.includes(user.user_id)}
                          onCheckedChange={() => toggleUser(user.user_id)}
                        />
                        <span className="text-sm">{user.name} ({user.email})</span>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}

              {/* Add Groups */}
              {availableGroups.length > 0 && (
                <div className="space-y-2">
                  <Label>User Groups</Label>
                  <ScrollArea className="h-32 border rounded-md p-2">
                    {availableGroups.map(group => (
                      <div 
                        key={group.group_id}
                        className="flex items-center gap-2 py-1"
                      >
                        <Checkbox
                          checked={selectedGroups.includes(group.group_id)}
                          onCheckedChange={() => toggleGroup(group.group_id)}
                        />
                        <span className="text-sm">{group.name} ({group.member_count || 0} members)</span>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {isAdmin && (selectedUsers.length > 0 || selectedGroups.length > 0) && (
            <Button 
              onClick={handleShare} 
              disabled={loading}
              data-testid="share-launchpad-folder-submit"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Share Selected
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Add/Edit Link Dialog
export const LaunchpadLinkDialog = ({
  open,
  onOpenChange,
  folder,
  link, // null for create, object for edit
  onSubmit,
  loading
}) => {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (link) {
      setTitle(link.title || '');
      setUrl(link.url || '');
      setDescription(link.description || '');
    } else {
      setTitle('');
      setUrl('');
      setDescription('');
    }
  }, [link, open]);

  const handleSubmit = () => {
    if (!title.trim() || !url.trim()) return;
    
    // Ensure URL has protocol
    let finalUrl = url.trim();
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }
    
    onSubmit({
      title: title.trim(),
      url: finalUrl,
      description: description.trim() || null
    });
  };

  const isEdit = !!link;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="launchpad-link-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Link' : `Add Link to "${folder?.name}"`}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="link-title">Title *</Label>
            <Input
              id="link-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Google"
              data-testid="launchpad-link-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="link-url">URL *</Label>
            <Input
              id="link-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://google.com"
              data-testid="launchpad-link-url-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="link-description">Description (Optional)</Label>
            <Textarea
              id="link-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Search engine"
              rows={2}
              data-testid="launchpad-link-description-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!title.trim() || !url.trim() || loading}
            data-testid="launchpad-link-submit"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? 'Save' : 'Add Link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Confirm Delete Dialog
export const ConfirmDeleteLaunchpadDialog = ({
  open,
  onOpenChange,
  itemType, // 'folder' or 'link'
  itemName,
  onConfirm,
  loading
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-delete-launchpad-dialog">
        <DialogHeader>
          <DialogTitle>Delete {itemType === 'folder' ? 'Folder' : 'Link'}</DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete "{itemName}"?
            {itemType === 'folder' && ' All links inside will also be deleted.'}
            This action cannot be undone.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            variant="destructive"
            onClick={onConfirm} 
            disabled={loading}
            data-testid="confirm-delete-launchpad-btn"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
